import React, { ReactNode, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { isMobile } from 'react-device-detect';
import { Button, Layout, Menu, Badge, Flex } from 'antd';
import {
    SettingFilled,
    NotificationFilled,
    FolderOpenFilled,
    FileTextOutlined,
    SendOutlined,
    MobileOutlined,
    LineChartOutlined,
    TeamOutlined,
    BuildOutlined,
} from '@ant-design/icons';

import { isNewYearAroundUs } from '@extensions/utils';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { setCurrentMenuItem, cleanupStates, setRestartServiceRequired } from '@store/actions';
import { serverFetch } from '@src/server';

import { IUserSession } from '@src/entities/user-session';

import Garland from '@core/controls/easter-eggs/garland/garland';

import { Permission, hasPermission } from '@src/enums/permission';
import {
    WarehouseIcon,
    AcceptanceIcon,
    LogisticsIcon,
    AirplaneIcon,
    DeliveryTruckIcon,
    BoxesIcon,
    LoaderIcon,
    AuditReportIcon,
    TariffIcon,
    CustomerRateIcon,
    BillIcon,
    UsdIcon,
    AccountingIcon,
    MiniLogoIcon,
    MainLogoIcon,
} from '@icons/index';

import '@core/index.css';

const getDefaultOpenKey = (menuItem: string) => {
    switch (menuItem) {
        case 'import':
        case 'delivery':
        case 'importpreloading':
            return 'logistics';

        case 'acceptance':
        case 'extraconsignments':
        case 'remainings':
        case 'shipmentjournals':
            return 'warehouse';

        case 'balances':
        case 'bills':
        case 'tariffs':
        case 'waybills':
            return 'finances';

        case 'usersstatistic':
        case 'importstatistic':
            return 'statistics';

        case 'newnotification':
        case 'push':
            return 'notifications';

        case 'countries':
        case 'cities':
        case 'warehouses':
        case 'consignees':
        case 'users':
        case 'counterparties':
        case 'contacts':
        case 'accounts':
        case 'items':
        case 'companies':
        case 'cargocarriers':
            return 'settings';

        default:
            return '';
    }
};
interface IMainLayout {
    appVersion: string | undefined;
}

const MainLayout = (props: IMainLayout) => {
    const { appVersion } = props;

    const { Header, Content, Sider } = Layout;

    const navigate = useNavigate();
    const location = useLocation();

    const d = useAppDispatch();
    const currentMenuItem = useAppSelector<string>((s) => s.currentMenuItem);
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const restartServiceRequired = useAppSelector<boolean>((s) => s.restartServiceRequired);

    const [defaultOpenKey, setDefaultOpenKey] = useState(getDefaultOpenKey(currentMenuItem));

    const [viewSetup] = useState<boolean>(hasPermission(userSession.permissions, Permission.ViewSetup));
    const [viewAudit] = useState<boolean>(
        hasPermission(userSession.permissions, Permission.ManageWarehouse) ||
            hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
            hasPermission(userSession.permissions, Permission.ManageBalance),
    );

    const [siderCollapsed, setSiderCollapsed] = useState<boolean>(isMobile);

    function getItem(label: string | ReactNode, key: string, urlTo?: string, icon?: React.JSX.Element, children?: Array<any>) {
        const itemlabel = urlTo ? <Link to={urlTo}>{label}</Link> : label;

        return {
            key,
            icon,
            children,
            label: itemlabel,
        };
    }

    useEffect(() => {
        const items = location.pathname.split('/');

        let key;
        switch (items[1]) {
            case 'logistics':
            case 'settings':
            case 'warehouse':
            case 'finance':
            case 'notifications':
            case 'statistics':
                key = items[2];
                break;
            default:
                key = items[1];
        }

        d(setCurrentMenuItem(key));
    }, [location]);

    const onLogout = () => {
        serverFetch(`auth/logout`, { method: 'POST' })
            .then(() => {
                d(cleanupStates());
                navigate('/');
            })
            .catch(() => {
                d(cleanupStates());
                navigate('/');
            });
    };

    useEffect(() => {
        setDefaultOpenKey(getDefaultOpenKey(currentMenuItem));
    }, [currentMenuItem]);

    const items = [
        getItem('Логистика', 'logistics', undefined, <LogisticsIcon />, [
            getItem('Импорт', 'import', '/logistics/import', <AirplaneIcon style={{ color: '#FFFFFF' }} />),
            getItem('Доставка', 'delivery', '/logistics/delivery', <DeliveryTruckIcon />),
            getItem('Предзагрузка', 'preloading', '/logistics/preloading', <BuildOutlined />),
        ]),
        getItem('Склад', 'warehouse', undefined, <WarehouseIcon style={{ fontSize: 16 }} />, [
            getItem('Приемка', 'acceptance', '/warehouse/acceptance', <AcceptanceIcon />),
            getItem('Остатки', 'remainings', '/warehouse/remainings', <BoxesIcon />),
            getItem('Отгрузка', 'shipmentjournals', '/warehouse/shipmentjournals', <LoaderIcon />),
        ]),
    ];

    if (
        hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
        hasPermission(userSession.permissions, Permission.EditTransportBill) ||
        hasPermission(userSession.permissions, Permission.ViewTransportBill) ||
        hasPermission(userSession.permissions, Permission.SetPaid) ||
        hasPermission(userSession.permissions, Permission.ManageTariff) ||
        hasPermission(userSession.permissions, Permission.ManageBalance) ||
        hasPermission(userSession.permissions, Permission.ViewBalance) ||
        hasPermission(userSession.permissions, Permission.ManageWaybill)
    ) {
        const finItems = [];

        if (
            hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
            hasPermission(userSession.permissions, Permission.EditTransportBill) ||
            hasPermission(userSession.permissions, Permission.ViewTransportBill) ||
            hasPermission(userSession.permissions, Permission.SetPaid)
        ) {
            finItems.push(getItem('Счета на оплату', 'bills', '/finance/bills', <BillIcon style={{ fontSize: 16 }} />));
        }

        if (
            hasPermission(userSession.permissions, Permission.ManageBalance) ||
            hasPermission(userSession.permissions, Permission.ViewBalance)
        ) {
            finItems.push(getItem('Балансы', 'balances', '/finance/balances', <AccountingIcon style={{ fontSize: 20 }} />));
        }

        if (hasPermission(userSession.permissions, Permission.ManageTariff)) {
            finItems.push(getItem('Тарифы', 'tariffs', '/finance/tariffs', <TariffIcon />));
        }

        if (hasPermission(userSession.permissions, Permission.ManageWaybill)) {
            finItems.push(getItem('AWB', 'waybills', '/finance/waybills', <FileTextOutlined style={{ fontSize: 16 }} />));
        }

        items.push(getItem('Финансы', 'finances', undefined, <UsdIcon style={{ fontSize: 16 }} />, finItems));
    }

    items.push(getItem('Документы', 'documents', '/documents', <FolderOpenFilled />));

    items.push(
        getItem('Уведомления', 'notifications', undefined, <NotificationFilled />, [
            getItem('Новое сообщение', 'newnotification', '/notifications/newnotification', <SendOutlined />),
            getItem('PUSH', 'push', '/notifications/push', <MobileOutlined style={{ fontSize: 16 }} />),
        ]),
    );

    if (viewSetup) {
        items.push(
            getItem('Управление', 'settings', undefined, <SettingFilled />, [
                getItem('Грузополучатели', 'consignees', '/settings/consignees'),
                getItem('Пользователи', 'users', '/settings/users'),
                getItem('Контрагенты', 'counterparties', '/settings/counterparties'),
                getItem('Теги', 'tags', '/settings/tags'),
                getItem('Города', 'cities', '/settings/cities'),
                getItem('Транспортные перевозчики', 'cargocarriers', '/settings/cargocarriers'),
                getItem('Страны', 'countries', '/settings/countries'),
                getItem('Продукты', 'items', '/settings/items'),
                getItem('Расчетные счета', 'accounts', '/settings/accounts'),
                getItem('Компании', 'companies', '/settings/companies'),
                getItem('Контакты', 'contacts', '/settings/contacts'),
            ]),
        );
    }

    if (viewAudit) {
        items.push(getItem('Аудит логи', 'auditlogs', '/auditlogs', <AuditReportIcon />));
    }

    const onMenuClick = (e: any) => {
        d(setCurrentMenuItem(e.key));
    };

    const userMenuItems = [
        getItem(
            <div className='user' style={{ cursor: 'pointer', textAlign: 'center', minWidth: 200 }}>
                {userSession.fullName}
            </div>,
            'account',
            undefined,
            undefined,
            [getItem('Личный кабинет', 'useraccount', '/useraccount', undefined)],
        ),
    ];

    const getLogo = () => {
        return siderCollapsed ? (
            <div className='logo'>
                <MiniLogoIcon style={{ fontSize: 30, display: 'table-cell', verticalAlign: 'middle' }} />
            </div>
        ) : (
            <div className='logo'>
                <div className='sub-logo'>
                    <div style={{ marginTop: 5 }}>ASIA HUB</div>
                </div>
                <MainLogoIcon style={{ fontSize: 38, display: 'table-cell', verticalAlign: 'middle' }} />
            </div>
        );
    };

    return (
        <Layout style={{ height: '100vh', minHeight: '100vh' }}>
            <Sider
                collapsible={!isMobile}
                collapsed={siderCollapsed}
                onCollapse={(e) => setSiderCollapsed(e)}
                width={250}
                style={{
                    overflow: 'auto',
                    height: '100vh',
                    zIndex: 100,
                }}
            >
                {getLogo()}
                <Flex style={{ height: 'calc(100vh - 120px)' }} vertical justify='space-between'>
                    <Menu
                        inlineIndent={12}
                        defaultOpenKeys={[defaultOpenKey]}
                        selectedKeys={[currentMenuItem]}
                        mode='inline'
                        items={items}
                        onClick={onMenuClick}
                    />
                    <div className='app-version'>Версия {appVersion}</div>
                </Flex>
            </Sider>
            <Layout>
                <Header className='main-header'>
                    <Flex align='center' justify='flex-end' gap='small'>
                        {isNewYearAroundUs() && <Garland />}
                        <Flex align='center' style={{ background: '#ffffff', position: 'relative' }}>
                            <Menu items={userMenuItems} mode='horizontal' />
                            <Button variant='link' size='large' color='blue' className='logout' onClick={() => onLogout()}>
                                Выход
                            </Button>
                        </Flex>
                    </Flex>
                </Header>
                <Content className='main-content'>
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
    );
};

export default MainLayout;
